import React from 'react';

/**
 * The property's county installments, read-only, with what has actually been
 * remitted against each.
 *
 * EVERY JUDGEMENT HERE IS THE SERVER'S. Whether an installment is settled, and
 * whether it is past due, are decided by lib/escrow.js and handed over on the
 * tax-schedule endpoint -- the same rule the dashboard card, the escrow report,
 * the PDF and the daily digest already run. This file chooses wording and
 * colour and nothing else, so the modal cannot drift away from the dashboard
 * the way a second copy of the settlement rule would.
 *
 * A line is omitted when the property carries no date or no amount for that
 * installment: a schedule nobody has entered is not the same thing as one that
 * is unpaid, and saying "Unpaid" about it would be a claim we cannot support.
 *
 * Status chips are suppressed entirely when the server reports
 * status_available false, which happens when the property carries more than one
 * active loan and the remittance cannot be attributed to one of them. The dates
 * and amounts are still useful there; a "Paid" belonging to another buyer's
 * loan would not be.
 */
function TaxInstallmentLines({ schedule }) {
  if (!schedule) return null;

  const lines = (schedule.instalments || []).filter(row => row.due_date && row.amount > 0);
  const variance = schedule.variance;
  const showVariance = Boolean(variance && variance.applicable && !variance.matches);

  if (lines.length === 0 && !showVariance) return null;

  const money = (value) => Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const day = (value) => new Date(value).toLocaleDateString();

  const chipFor = (row) => {
    if (!schedule.status_available) return null;

    const base = {
      display: 'inline-block',
      marginLeft: '8px',
      padding: '1px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: 'bold',
      whiteSpace: 'nowrap'
    };

    if (row.settled) {
      return (
        <span style={{ ...base, background: 'var(--color-success-bg)', color: 'var(--color-success-text)' }}>
          {row.settled_on ? `Paid ${day(row.settled_on)}` : 'Paid'}
        </span>
      );
    }
    if (row.past_due) {
      return (
        <span style={{ ...base, background: 'var(--color-error-bg)', color: 'var(--color-error-text)' }}>Past due</span>
      );
    }
    return (
      <span style={{ ...base, background: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' }}>Unpaid</span>
    );
  };

  return (
    <div style={{ marginTop: '10px' }}>
      {lines.map((row) => (
        <div
          key={row.instalment}
          style={{ fontSize: '13px', color: 'var(--color-text)', marginTop: '4px', lineHeight: '1.5' }}
        >
          {row.instalment === 1 ? '1st' : '2nd'} installment: ${money(row.amount)}
          {' '}due {day(row.due_date)}
          {chipFor(row)}
        </div>
      ))}

      {/* Reported, never blocking. A variance is frequently legitimate -- a
          supplemental assessment, a mid-year re-bill -- and the admin is
          recording what the county actually sent either way. */}
      {showVariance && (
        <div style={{
          marginTop: '8px',
          padding: '8px 10px',
          borderRadius: '5px',
          backgroundColor: 'var(--color-warning-bg)',
          color: 'var(--color-warning-text)',
          fontSize: '12px'
        }}>
          The two installments add up to ${money(variance.scheduled)}, which is
          {' '}${money(Math.abs(variance.variance))} {variance.variance > 0 ? 'more' : 'less'}
          {' '}than the annual tax amount of ${money(variance.annual)}.
        </div>
      )}
    </div>
  );
}

export default TaxInstallmentLines;
